import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { CheckCircle, KeyboardReturn } from '@material-symbols-svg/react/rounded/w500';
import { memo, type ReactNode, useCallback } from 'react';

import { palette, tokens } from '../../theme';

export type SearchResultRowProps = {
    id: number;
    title: string;
    selected: boolean;
    // Left segment — the assignee.
    avatar?: ReactNode;
    // First line, before the id.
    stateDot?: ReactNode;
    // First line, after the id.
    badge?: ReactNode;
    onSelect: () => void;
};

export const SearchResultRow = memo(function SearchResultRow({
    id,
    title,
    selected,
    avatar,
    stateDot,
    badge,
    onSelect,
}: SearchResultRowProps) {
    const classes = useStyles();
    const handleClick = useCallback(() => onSelect(), [onSelect]);

    return (
        <button
            type="button"
            className={mergeClasses(classes.root, selected && classes.selected)}
            onClick={handleClick}
            disabled={selected}
            aria-disabled={selected}
            title={selected ? 'Already in plan' : undefined}
        >
            {avatar ? <span className={classes.avatar}>{avatar}</span> : <span aria-hidden />}
            <span className={classes.meta}>
                <span className={classes.metaTop}>
                    {stateDot}
                    <span className={classes.idText}>#{id}</span>
                    {badge}
                </span>
                <span className={classes.title}>{title}</span>
            </span>
            {selected ? (
                <span className={classes.addedBadge}>
                    <CheckCircle className={classes.addedIcon} aria-hidden />
                    <span>Added</span>
                </span>
            ) : (
                <KeyboardReturn className={classes.returnIcon} aria-hidden />
            )}
        </button>
    );
});

const useStyles = makeStyles({
    root: {
        display: 'grid',
        // assignee | id/state/type over title | trailing
        gridTemplateColumns: '32px minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        width: '100%',
        marginBottom: '8px',
        padding: '10px 14px',
        backgroundColor: palette.panelBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
        '&:hover': {
            backgroundColor: palette.panelMuted,
        },
    },
    selected: {
        backgroundColor: palette.accentBg,
        border: `1px solid ${palette.accentBgStrong}`,
        cursor: 'default',
        '&:hover': {
            backgroundColor: palette.accentBg,
        },
    },
    avatar: {
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
    },
    meta: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: 0,
    },
    metaTop: {
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        height: '18px',
        minWidth: 0,
    },
    idText: {
        fontSize: '12px',
        color: tokens.colorNeutralForeground3,
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    title: {
        fontSize: '13px',
        color: tokens.colorNeutralForeground1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
    },
    addedBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '12px',
        fontWeight: 500,
        color: palette.accentText,
        flexShrink: 0,
    },
    addedIcon: {
        width: '14px',
        height: '14px',
    },
    returnIcon: {
        width: '14px',
        height: '14px',
        color: palette.dimText,
        flexShrink: 0,
    },
});
